import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import DyteClient from '@dytesdk/web-core';
import { DyteComponentsModule, DyteMeeting } from '@dytesdk/angular-ui-kit';

import { VideoPageService } from './video-page.service';
import { PATH } from '../../../../../assets/constants';
import { AuthenticationService } from 'src/app/shared/services/authentication.service';
import { ShowPopupService } from '../../../../shared/services/show-popup.service';

@Component({
  selector: 'app-video-call',
  templateUrl: './video-page.component.html',
  styleUrls: ['./video-page.component.scss'],
  imports: [ DyteComponentsModule ],
  standalone: true
})
export class VideoPageComponent implements OnInit {
  private videoPageService = inject( VideoPageService );
  private popupService = inject( ShowPopupService );
  private authService = inject( AuthenticationService );
  private route = inject( ActivatedRoute );
  private router = inject( Router );

  meetingId: string = '';

  @ViewChild( 'meeting' ) $meetingEl!: DyteMeeting;

  meeting: DyteClient | null = null;

  origin: string = '';
  id: string = '';

  userId: string = '';
  applicationId: string = '';
  jobOffer = '';

  guestName = '';
  guestId = '';

  constructor() {
    this.id = this.safeGetUserId();
  }

  async ngOnInit() {
    this.route.queryParams.subscribe( ( queryParams ) => {
      this.userId = queryParams['userId'] || '';
      this.applicationId = queryParams['applicationId'] || '';
      this.jobOffer = queryParams['jobOffer'] || '';
      this.origin = queryParams['origin'] || '';

      this.guestName = queryParams['guestName'] || '';
      this.guestId = queryParams['guestId'] || '';
    } );

    this.route.params.subscribe( async ( params ) => {
      this.meetingId = params.id;

      if ( !this.meetingId ) return;

      try {
        const authToken = await this.resolveAuthToken();

        if ( !authToken ) {
          alert( 'Failed to retrieve auth token to join the meeting.' );
          return;
        }

        this.meeting = await DyteClient.init( { authToken } );
        this.$meetingEl.meeting = this.meeting;

        this.meeting?.self?.on?.( 'roomLeft', () => {
          this.handleMeetingEnd();
        } );

      } catch ( error ) {
        console.error( 'Error joining meeting:', error );
        this.popupService.showFailurePopup( { translationKey: 'popup.meeting.joinError' } );
      }
    } );
  }


  private async resolveAuthToken(): Promise<string | null> {
    const isLoggedIn = this.isUserLoggedIn();

    if ( !isLoggedIn ) {
      const name = this.guestName?.trim() || 'Guest';
      const id = this.guestId?.trim() || this.createGuestId();

      this.guestName = name;
      this.guestId = id;

      const guestTokenRes = await this.videoPageService
        .getGuestToken( this.meetingId, name, id )
        .toPromise();

      return guestTokenRes?.token || null;
    }

    const res = await this.videoPageService
      .addParticipant( this.meetingId )
      .toPromise();

    return res?.data?.token || null;
  }

  private isUserLoggedIn(): boolean {
    const tokenGetter = ( this.authService as any )?.getAccessToken;
    const token = typeof tokenGetter === 'function' ? tokenGetter.call( this.authService ) : null;
    return !!token;
  }

  private safeGetUserId(): string {
    try {
      return this.authService.getIdFromAccessToken?.() || '';
    } catch {
      return '';
    }
  }

  private createGuestId(): string {
    return crypto.randomUUID();
  }

  handleMeetingEnd() {
    if ( this.applicationId ) {
      this.checkIfAllParticipantsLeft( this.applicationId );
    }

    this.navigateBack();
  }

  checkIfAllParticipantsLeft( applicationId: string ) {
    const remainingParticipants = this.meeting?.participants?.joined?.size;
    console.log( 'Remaining participants:', remainingParticipants );

    if ( remainingParticipants === 0 ) {
      this.sendMeetingFinishedMessage( applicationId );
    }
  }

  sendMeetingFinishedMessage( applicationId: string ) {
    console.log( 'Meeting finished and notification sent to backend:' );
    this.videoPageService.notifyMeetingFinished( applicationId ).subscribe( ( response ) => {
      console.log( response );
    } );
  }

  navigateBack() {
    if ( !this.isUserLoggedIn() ) {
      this.router.navigate( [ PATH.MAIN ] );
      return;
    }

    const isCompany = !!this.authService.getCompanyIdFromAccessToken?.();

    if ( isCompany ) {
      this.router.navigate( [ `${PATH.JOBS}/${this.jobOffer}` ], {
        state: { fromInterview: true, applicantId: this.applicationId },
      } );
    } else {
      this.router.navigate( [ `${PATH.MAIN}/${this.id}` ] );
    }
  }
}
